import { Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ActCategory } from 'shell/models/act-category-list';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useEventCallback } from '@mui/material';
import { IdStateNotification } from 'notifications-from-civil-status/constants/id-state-notification';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import CancellationDueDeathPage from 'cancellations/pages/cancellation-due-death/cancellation';
import { CancellationDueDeath } from 'cancellations/view-models/cancellation-due-death';
import { transformDeath } from 'notifications-from-civil-status/utils/transform-death';
import ErrorModal from 'shell/components/shared/modal-error';

interface CancellationDeathNotificationSharedProps {
  goBackUrl?: string;
  items?: BreadcrumbItem[];
  goBackUrlDetail?: string;
}

const CancellationDeathNotificationShared = ({
  goBackUrl = '',
  items = [],
  goBackUrlDetail = '',
}: CancellationDeathNotificationSharedProps): ReactElement => {
  const [modalError, setModalError] = useState<string>('');

  const params = useParams();

  const idAnsc = useMemo(() => {
    return params.id || undefined;
  }, [params.id]);

  const idNotifica = params.idNotifica || '';

  const [dataEvent, getDataEvent] = useHttpFetch<HttpResponse<ActCategory>, CancellationDueDeath>(
    `${__ACT_API_URL__}/consultazione/evento`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: (res) => {
        const castedRes = transformDeath(res);
        if (castedRes.error) {
          setModalError(castedRes.error);
        }
        return castedRes.data;
      },
    }
  );

  useEffect(() => {
    if (idAnsc) {
      getDataEvent({
        data: {
          criteriRicercaEvento: {
            idAnsc,
          },
        },
      });
    }
  }, [idAnsc]);

  const saveData = useHttpClient(`${__ACT_API_URL__}/aggiornamento-anagrafe/save`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const navigate = useNavigate();

  const onConfirm = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveData({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.RegistrazioneEffettuata,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate(goBackUrlDetail);
    });
  });

  const onCancel = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveData({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.DaRegistrare,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate(goBackUrl);
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={items} />
      {dataEvent.data && !dataEvent.pristine && !dataEvent.isLoading && !dataEvent.hasErrors ? (
        <CancellationDueDeathPage
          isWithoutBreadcrumbs
          initialValues={dataEvent.data as CancellationDueDeath}
          onConfirm={onConfirm}
          onCancel={onCancel}
          disabledFromAct
        />
      ) : undefined}
      {Boolean(modalError) ? (
        <ErrorModal
          message={modalError}
          onClose={() => {
            setModalError('');
            navigate(goBackUrl || '');
          }}
          open={Boolean(modalError)}
        />
      ) : undefined}
    </Fragment>
  );
};

CancellationDeathNotificationShared.displayName = 'Cancellation Death Notification Page component';

export default CancellationDeathNotificationShared;
